import { ButtonTypes, IconButton, Button, Tooltip } from '@genial/components';
import { BoxRadius, Color, ColorTypes, Spacing, Typography } from '@genial/design-tokens';
import { IconographyTypes } from '@genial/icons';
import { Utils, useBreakpointValue } from '@genial/utils';
import { Box } from 'native-base';
import React from 'react';

import { AssetSelectedTypeMap } from '../../../../../store/product/types';
import { ProductsType } from '../../../../BatchTicket/components/DynamicAsset/constants';
import { ProductButtonProps } from '../../../../BatchTicket/components/DynamicButton';

export interface ResponsiveButtonProps<T extends keyof AssetSelectedTypeMap>
  extends ProductButtonProps<T> {
  text: string;
  color: ColorTypes.Option;
  icon: IconographyTypes.Option;
  mobileIconColor: ColorTypes.Option;
  mobileButtonIconColor?: ColorTypes.Option;
  onPress: () => void;
  buttonVariant?: ButtonTypes.Variant;
  backgroundColor?: ColorTypes.Option;
  tooltip?: string;
  isLoading?: boolean;
}

export const ResponsiveButton: React.FC<ResponsiveButtonProps<ProductsType>> = ({
  text,
  icon,
  color,
  onPress,
  backgroundColor,
  tooltip,
  isDisabled,
  isLoading,
  buttonVariant = 'callToAction',
  mobileIconColor,
  mobileButtonIconColor,
}) => {
  const buttonWidthRef = React.useRef<number>();

  const button = useBreakpointValue({
    base: (
      <IconButton
        icon={icon}
        testId="return-button"
        iconColor={Color[mobileIconColor]}
        iconSize="xs"
        backgroundColor={Color[mobileButtonIconColor || color]}
        borderRadius={BoxRadius.sm}
        h={Spacing['inline.md']}
        w={Spacing['inline.md']}
        shadow={3}
        onPress={onPress}
        isDisabled={isDisabled || isLoading}
      />
    ),
    lg: (
      <Button
        testID="button"
        onLayout={(event) => (buttonWidthRef.current = event.nativeEvent.layout.width)}
        maxW={buttonWidthRef.current}
        variant={buttonVariant}
        isDisabled={isDisabled}
        bg={backgroundColor ? Color[backgroundColor] : undefined}
        _text={{
          color: Color[color],
          fontFamily: Typography['fontfamily.base.semibold'],
          fontWeight: Typography['fontweight.semibold'],
        }}
        _hover={
          backgroundColor && {
            bg: Utils.ColorToken.WithOpacity(backgroundColor, 'opacity.intense'),
          }
        }
        useNativeBaseLoading
        isLoading={isLoading}
        flex={isLoading ? 1 : undefined}
        spinnerPlacement="end"
        isLoadingText={text}
        onPress={onPress}>
        {text}
      </Button>
    ),
  });

  if (tooltip) {
    return (
      <Tooltip label={tooltip}>
        <Box>{button}</Box>
      </Tooltip>
    );
  }

  return button;
};
